/**
 * State fertility mandate hooks.
 *
 * Fetches state-level fertility insurance mandate data from the
 * Dashboard API. Used to show patients what their state requires
 * insurers to cover.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@louise/api/louise-api";
import {
  getApiResponseStatus,
  throwApiError,
} from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";

// ─── Types ──────────────────────────────────────────────────────────────────

/** A single state's fertility mandate, as returned by the Dashboard API. */
export type StateFertilityMandate = NonNullable<
  ResponseBody<paths["/coverages/state-mandates/{state}"]["get"]>
>["data"];

// ─── Query keys ─────────────────────────────────────────────────────────────

export const stateMandateKeys = {
  all: ["state-mandates"] as const,
  list: () => [...stateMandateKeys.all, "list"] as const,
  detail: (state: string) =>
    [...stateMandateKeys.all, "detail", state] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch the fertility mandate for a specific state.
 */
export function useStateMandateForState(stateCode: string | null) {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: stateMandateKeys.detail(stateCode ?? ""),
    queryFn: async () => {
      const result = await client.GET("/coverages/state-mandates/{state}", {
        params: {
          path: { state: stateCode ?? "" },
        },
      });
      const { data, error } = result;

      if (error) {
        throwApiError(
          error,
          "Failed to fetch state mandate",
          "useStateMandateForState",
          getApiResponseStatus(result),
        );
      }

      return data.data;
    },
    enabled: !!stateCode && !!tenantId,
  });
}

/**
 * Fetch all state mandates (for a map/overview screen).
 */
export function useAllStateMandates() {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: stateMandateKeys.list(),
    queryFn: async () => {
      const result = await client.GET("/coverages/state-mandates");
      const { data, error } = result;

      if (error) {
        throwApiError(
          error,
          "Failed to fetch state mandates",
          "useAllStateMandates",
          getApiResponseStatus(result),
        );
      }

      return data.data;
    },
    enabled: !!tenantId,
  });
}
